import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import LocationFilter from '../LocationFilter';
import LocationFilterDropdown from '../LocationFilter/components/LocationFilterDropdown';
import { trackEvent } from '../../../../../../components/Analytics/TrackedClick';
import { useEventSearch } from '../../hooks/useEventSearch';

import OpenRegistrationFilter from '../OpenRegistrationFilter';
import NowPlayingFilter from '../NowPlayingFilter';
import UpcomingEventsFilter from '../UpcomingEventsFilter';
import AllEventSeriesFilter from '../AllEventSeriesFilter';
import SportTypeFilter from '../SportTypeFilter';
import MoreFilters from '../MoreFilters';
import MobileModal from '../../../SharedComponents/MobileModal';
import FilterButton from '../../../SharedComponents/FilterButton';

import {
    checkMoreFiltersActive,
    checkAllEventSeriesFiltersActive,
} from '../../utils/filterHelpers';
import { TRACKING_LABELS } from '../../../constants';

import styles from '../../../searchV2.scss';
import eventFiltersStyles from './eventFilters.scss';

const EventFilters = ({
    filters,
    locationInputValue,
    updateFilters,
    onFilterSubmit,
    onClearFilters,
    auth,
    userLoggedIn,
    eventCount,
    hasQueryParam,
    searchTypes,
    onLocationChange,
    onLocationSelect,
    onCurrentLocationClick,
    onDistanceChange,
    onApplyLocationFilter,
}) => {
    const [showMoreFilters, setShowMoreFilters] = useState(false);

    const [showLocationModal, setShowLocationModal] = useState(false);
    const [hasResetFilters, setHasResetFilters] = useState(false);
    const [allEventsOptions, setAllEventsOptions] = useState([]);
    const filtersContainerRef = useRef(null);
    const { tours } = useEventSearch();

    const showTennisContent = auth?.feedPreferences?.showTennisContent;
    const showPickleballContent = !!auth?.feedPreferences?.showPickleballContent;
    const showTennisFilters = !!filters?.sportTypes?.tennis;
    const showPickleballFilters = !!filters?.sportTypes?.pickleball;
    const isUserLoggedInAndToursAvailable = allEventsOptions?.length > 0 && userLoggedIn;
    const isMoreFilterActive = checkMoreFiltersActive(
        filters,
        showTennisFilters,
        showPickleballFilters,
    );
    const isAllEventSeriesFilterActive = checkAllEventSeriesFiltersActive(filters);
    const shouldShowResetFilters = isMoreFilterActive || isAllEventSeriesFilterActive;
    // Simplified props for filter components
    const commonFilterProps = {
        filters,
        handleFilterUpdate: (newFilters, updateType = 'update') => {
            updateFilters(newFilters);
            if (updateType === 'submit') {
                // Pass the updated filters to the parent for immediate search
                const updatedFilters = { ...filters, ...newFilters };
                onFilterSubmit(updatedFilters);
            }
        },
        auth, // Needed by some components for user preferences
    };

    useEffect(() => {
        if (tours?.allEventTours?.length > 0 && tours?.availableTours?.length > 0) {
            getTourOptions(tours.allEventTours, tours.availableTours);
        }
    }, [tours, filters?.sportTypes]);

    const getTourOptions = (allEventTours, availableTours) => {
        if (!userLoggedIn || !availableTours?.length || !allEventTours?.length) {
            return [];
        }
        const tennisSportTypeIsSelected = filters?.sportTypes?.tennis;
        const pickleballSportTypeIsSelected = filters?.sportTypes?.pickleball;

        let filteredTours = availableTours;

        // Filter by sport type
        if ((tennisSportTypeIsSelected && !pickleballSportTypeIsSelected) ||
            (!pickleballSportTypeIsSelected && !showPickleballContent && showTennisContent)) {
            filteredTours = availableTours.filter((tour) => {
                const foundTour = allEventTours.find((newTour) => newTour.name === tour.value);
                return foundTour && !!foundTour.isTennisTour;
            });
        } else if ((pickleballSportTypeIsSelected && !tennisSportTypeIsSelected) ||
                   (!tennisSportTypeIsSelected && !showTennisContent && showPickleballContent)) {
            filteredTours = availableTours.filter((tour) => {
                const foundTour = allEventTours.find((newTour) => newTour.name === tour.value);
                return foundTour && !!foundTour.isPickleballTour;
            });
        }

        // Map to the expected format
        const options = filteredTours.map((tour) => ({
            value: tour.value,
            label: tour.label || tour.value,
        }));
        setAllEventsOptions(options);
    };

    const handleOnMoreFilterClick = () => {
        setShowMoreFilters(true);
    };

    const handleCloseMoreFilters = () => {
        setShowMoreFilters(false);
    };

    // Mobile modal handlers
    const handleLocationClick = () => {
        setShowLocationModal(true);
    };

    const handleCloseLocationModal = () => {
        setShowLocationModal(false);
    };

    // Shared location logic for both mobile and desktop
    const applyLocationWithClearing = () => {
        const input = locationInputValue;

        // Delegate clearing vs. applying to parent handler
        onApplyLocationFilter();

        // Track only when there is a non-empty input
        if (input && input.trim() !== '') {
            const { LOCATION, CLIENT, EVENT_NAME } = TRACKING_LABELS;
            trackEvent(EVENT_NAME, {
                filter_name: LOCATION,
                filter_value: input,
                client: CLIENT,
            });
        }
    };

    const handleLocationApply = () => {
        applyLocationWithClearing();
        setShowLocationModal(false);
    };

    const handleResetFilters = () => {
        setHasResetFilters(true);
        onClearFilters();
    };

    const renderLocationMobileModalContent = () => {
        return (
            <div style={{ padding: '20px' }}>
                <LocationFilterDropdown
                    value={locationInputValue || ''}
                    geoLocation={null}
                    handleLocationChange={onLocationChange}
                    handleLocationSelect={onLocationSelect}
                    handleCurrentLocationClick={onCurrentLocationClick}
                    distance={filters.distance}
                    handleDistanceChange={onDistanceChange}
                />
            </div>
        );
    };

    const getEventResultsText = () => {
        if (eventCount === 0) {
            return '';
        }
        const tennis = 'Tennis';
        const pickleball = 'Pickleball';
        const hasPickleballFilter = !!filters?.sportTypes?.pickleball;
        const hasTennisFilter = !!filters?.sportTypes?.tennis;

        let sportTypeText = '';
        if (!hasQueryParam) {
            if (hasPickleballFilter && !hasTennisFilter) {
                sportTypeText = pickleball;
            } else if (hasTennisFilter && !hasPickleballFilter) {
                sportTypeText = tennis;
            }
        }

        return eventCount === 1 ? `${eventCount} ${sportTypeText} Event` : `${eventCount} ${sportTypeText} Events`;
    };

    const moreLabel = 'More...';

    return (
        <div className={eventFiltersStyles.eventFiltersContainer}>
            <div className={styles.searchFilters}>
                <div className={styles.afterLoading}>
                    <div className={styles.eventFiltersWrapper}>
                        {/* Mobile: Use scroll container */}
                        <div className={`${styles.eventFiltersScrollContainer} d-block d-md-none`} ref={filtersContainerRef}>
                            <div className={styles.eventFiltersScrollContent}>
                                <SportTypeFilter
                                    {...commonFilterProps}
                                    hasResetFilters={hasResetFilters}
                                />
                                <LocationFilter
                                    {...commonFilterProps}
                                    filters={{ ...filters, locationInputValue }}
                                    geocoded={null} // Would need to be passed from parent
                                    applyLocationFilter={applyLocationWithClearing}
                                    handleLocationChange={onLocationChange}
                                    handleLocationSelect={onLocationSelect}
                                    handleCurrentLocationClick={onCurrentLocationClick}
                                    handleDistanceChange={onDistanceChange}
                                    onMobileClick={handleLocationClick}
                                />
                                <OpenRegistrationFilter {...commonFilterProps} />
                                <NowPlayingFilter {...commonFilterProps} />
                                <UpcomingEventsFilter {...commonFilterProps} />
                                {isUserLoggedInAndToursAvailable && (
                                    <AllEventSeriesFilter
                                        {...commonFilterProps}
                                        tourOptions={allEventsOptions}
                                        hasResetFilters={hasResetFilters}
                                    />
                                )}
                                {userLoggedIn && (
                                    <FilterButton
                                        isActive={isMoreFilterActive}
                                        onClick={handleOnMoreFilterClick}
                                        data-testid="search.events.mobile.more-filters-btn"
                                    >
                                        {moreLabel}
                                    </FilterButton>
                                )}
                                {shouldShowResetFilters && (
                                    <u className={`${eventFiltersStyles.resetFiltersLinkMobile} clickable text-teal text-small`} onClick={handleResetFilters} data-testid="search.events.mobile.reset-filters-link">Reset Filters</u>
                                )}
                            </div>
                        </div>

                        {/* Desktop: No scroll container, filters wrap naturally */}
                        <div className={`${styles.eventFiltersDesktopContainer} d-none d-md-block`}>
                            <SportTypeFilter
                                {...commonFilterProps}
                                hasResetFilters={hasResetFilters}
                            />
                            <LocationFilter
                                {...commonFilterProps}
                                filters={{ ...filters, locationInputValue }}
                                geocoded={null} // Would need to be passed from parent
                                applyLocationFilter={applyLocationWithClearing}
                                handleLocationChange={onLocationChange}
                                handleLocationSelect={onLocationSelect}
                                handleCurrentLocationClick={onCurrentLocationClick}
                                handleDistanceChange={onDistanceChange}
                            />
                            <OpenRegistrationFilter {...commonFilterProps} />
                            <NowPlayingFilter {...commonFilterProps} />
                            <UpcomingEventsFilter {...commonFilterProps} />
                            {isUserLoggedInAndToursAvailable && (
                                <AllEventSeriesFilter
                                    {...commonFilterProps}
                                    tourOptions={allEventsOptions}
                                    hasResetFilters={hasResetFilters}
                                />
                            )}
                            {userLoggedIn && (
                                <>
                                    <FilterButton
                                        isActive={isMoreFilterActive}
                                        onClick={handleOnMoreFilterClick}
                                        data-testid="search.events.desktop.more-filters-btn"
                                    >
                                        {moreLabel}
                                    </FilterButton>
                                    <MoreFilters
                                        {...commonFilterProps}
                                        isVisible={showMoreFilters}
                                        onClose={handleCloseMoreFilters}
                                        searchTypes={searchTypes}
                                    />
                                </>
                            )}
                            {shouldShowResetFilters && (
                                <u className={`${eventFiltersStyles.resetFiltersLink} clickable text-teal text-small`} onClick={handleResetFilters} data-testid="search.events.desktop.reset-filters-link">Reset Filters</u>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.eventCountContainer}>
                <div className={styles.eventCountText}>
                    <span className={styles.eventCountText}>{getEventResultsText()}</span>
                </div>
            </div>

            {/* Mobile Modals - rendered at top level */}
            <MobileModal
                isOpen={showLocationModal}
                onClose={handleCloseLocationModal}
                title="Location"
                onApply={handleLocationApply}
            >
                {renderLocationMobileModalContent()}
            </MobileModal>
            {userLoggedIn && (
                <MoreFilters
                    {...commonFilterProps}
                    isVisible={showMoreFilters}
                    onClose={handleCloseMoreFilters}
                    searchTypes={searchTypes}
                />
            )}
        </div>
    );
};

EventFilters.propTypes = {
    filters: PropTypes.instanceOf(Object).isRequired,
    locationInputValue: PropTypes.string,
    updateFilters: PropTypes.func.isRequired,
    onFilterSubmit: PropTypes.func.isRequired,
    onClearFilters: PropTypes.func.isRequired,
    auth: PropTypes.instanceOf(Object),
    userLoggedIn: PropTypes.bool,
    eventCount: PropTypes.number,
    hasQueryParam: PropTypes.bool,
    searchTypes: PropTypes.instanceOf(Object),
    onLocationChange: PropTypes.func,
    onLocationSelect: PropTypes.func,
    onCurrentLocationClick: PropTypes.func,
    onDistanceChange: PropTypes.func,
    onApplyLocationFilter: PropTypes.func,
};

EventFilters.defaultProps = {
    auth: null,
    locationInputValue: '',
    userLoggedIn: false,
    eventCount: 0,
    hasQueryParam: false,
    searchTypes: {},
    onLocationChange: () => {},
    onLocationSelect: () => {},
    onCurrentLocationClick: () => {},
    onDistanceChange: () => {},
    onApplyLocationFilter: () => {},
};

export default EventFilters;
